'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';

import { observeResizeRaf } from '@/hooks/useRafResizeObserver';
import { getTabbableElements } from '@/lib/a11y/tabbable';
import { LayerOwnerProvider, useLayerOwner, type LayerOwner } from '@/lib/layers/LayerOwner';
import { pushLayer } from '@/lib/layers/layerStack';
import { cn } from '@/lib/utils';

import { overlay } from './overlay-recipes';

/**
 * PopupPortal: a popup that is NEVER clipped by the container it was opened in.
 *
 * ROOT CAUSE it fixes (owner, 2026-10-22: "clicking the translation text
 * dropdown the popup is cut off by the container as it wont render outside the
 * container"): the translation-language and audio-preference panels were
 * `position: absolute` children of the control that opened them, so every
 * ancestor with `overflow: hidden|auto` (the control bar, the tools tray, the
 * mobile strip) cut them off, and nothing kept them inside the viewport.
 *
 * This renders the panel in a portal on `document.body` with `position: fixed`,
 * measured from the anchor's rectangle, so no ancestor overflow or stacking
 * context can reach it. It flips above/below and shifts sideways to stay inside
 * the viewport, and caps its height (scrolling inside) when neither side fits.
 *
 * Accessibility: Escape closes it and returns focus to the anchor's trigger
 * (`onEscape`, through the ONE layer stack, lib/layers/layerStack.ts: only the
 * top layer takes an Escape, and a modal that opens closes the popup first,
 * docs/THEME.md 2.10); Tab out of either end returns to the trigger (`onTabOut`) so a
 * keyboard user is never stranded in a portal that sits at the end of the DOM.
 * With `initialFocus`, opening moves focus to the panel's first Tab stop (UI
 * review 2026-10-23, in-call B04: the disclosure popups left focus on their
 * summary, so Tab walked PAST the portalled panel, which sits at the end of
 * the DOM, and in Call settings the dialog's loop skipped it altogether).
 * The panel carries `data-popup-portal` so a parent popup (the settings dialog)
 * knows pointer and keyboard activity inside it is still "inside".
 * Styling is the caller's `className` (dark theme tokens stay with the caller), or `surface="standard"`: the one popup
 * look (components/ui/overlay-recipes.ts) for migrated callers (std-overlays foundation).
 * Layer owner (docs/THEME.md 2.10): inside a migrated modal or the page layer root the panel renders in its owner's
 * slot (above the modal, inside its focus scope) and carries z-page-layer; with no owner it is the body portal as before.
 * `hostNestedPopups` (opt-in, std-overlays G18): the panel is itself the owner of the popups opened inside it (a Select
 * list, a menu, a tooltip). They render in the panel, above its content, so a press in them is not an outside press for
 * the panel (or for a dialog that treats the panel as its own), and Escape inside one closes that popup first.
 */

export interface PopupRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export interface PopupPlacementInput {
  anchor: PopupRect;
  /** Natural (uncapped) size of the popup. */
  panel: { width: number; height: number };
  viewport: { width: number; height: number };
  /** Preferred side; flips when the other side fits better. */
  prefer?: 'top' | 'bottom';
  /** Which anchor edge the popup lines up with before it is shifted. */
  align?: 'start' | 'end';
  gap?: number;
  margin?: number;
}

export interface PopupPlacement {
  top: number;
  left: number;
  maxHeight: number;
  side: 'top' | 'bottom';
}

const MIN_HEIGHT = 96;

/** Pure placement maths: flip, cap and shift. Exported for unit tests. */
export function computePopupPlacement(input: PopupPlacementInput): PopupPlacement {
  const { anchor, panel, viewport, prefer = 'top', align = 'end', gap = 8, margin = 8 } = input;
  const space = {
    top: anchor.top - gap - margin,
    bottom: viewport.height - anchor.bottom - gap - margin,
  };
  const other = prefer === 'top' ? 'bottom' : 'top';
  let side: 'top' | 'bottom' = prefer;
  if (panel.height > space[prefer] && space[other] > space[prefer]) side = other;
  const maxHeight = Math.max(Math.min(space[side], viewport.height - 2 * margin), MIN_HEIGHT);
  const height = Math.min(panel.height, maxHeight);
  const rawTop = side === 'top' ? anchor.top - gap - height : anchor.bottom + gap;
  const top = Math.min(Math.max(rawTop, margin), Math.max(margin, viewport.height - height - margin));
  const width = Math.min(panel.width, viewport.width - 2 * margin);
  const rawLeft = align === 'end' ? anchor.right - width : anchor.left;
  const left = Math.min(Math.max(rawLeft, margin), Math.max(margin, viewport.width - width - margin));
  return { top, left, maxHeight, side };
}

/** Open state for a native `<details>` whose panel is portalled out of it. */
export function useDisclosurePortal() {
  const ref = React.useRef<HTMLDetailsElement>(null);
  const [open, setOpen] = React.useState(false);
  const onToggle = React.useCallback((event: React.SyntheticEvent<HTMLDetailsElement>) => {
    setOpen(event.currentTarget.open);
  }, []);
  const close = React.useCallback(() => {
    if (ref.current) ref.current.open = false;
    setOpen(false);
    ref.current?.querySelector<HTMLElement>('summary')?.focus();
  }, []);
  return { ref, open, onToggle, close };
}

/** Panel element -> the anchor element it was opened from. */
const popupOwners = new WeakMap<Element, Element>();

export interface PopupPortalProps {
  panelRef?: React.MutableRefObject<HTMLDivElement | null>;
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  className?: string;
  children: React.ReactNode;
  testId?: string;
  prefer?: 'top' | 'bottom';
  align?: 'start' | 'end';
  gap?: number;
  role?: string;
  /** The panel's accessible name: it belongs on the element that HAS the role (WCAG 4.1.2). */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  /** Escape pressed while open. The caller closes and restores focus. */
  onEscape?: () => void;
  /** Pointer pressed outside both the anchor and the popup. */
  onOutside?: () => void;
  /** Move focus to the first Tab stop inside the panel each time it opens (a disclosure popup). Off for menus that
   *  manage their own focus. */
  initialFocus?: boolean;
  /** "standard": the shared popup surface (bg-popup, edge, rounded-overlay, shadow-float, p-1). Default: none. */
  surface?: 'none' | 'standard';
  /** The panel is at least as wide as the anchor (listboxes under a trigger). */
  matchAnchorWidth?: boolean;
  /** Portal target override. Default: the layer owner's slot, else document.body. */
  container?: HTMLElement | null;
  /** The panel owns the popups opened inside it (2.10): they render in it, and Escape in one closes that one first. */
  hostNestedPopups?: boolean;
}

export function PopupPortal({
  anchorRef,
  panelRef: externalPanelRef,
  open,
  className,
  children,
  testId,
  prefer = 'top',
  align = 'end',
  gap,
  role,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  onMouseEnter,
  onMouseLeave,
  onEscape,
  onOutside,
  initialFocus = false,
  surface = 'none',
  matchAnchorWidth = false,
  container,
  hostNestedPopups = false,
}: PopupPortalProps) {
  const owner = useLayerOwner();
  const ownerSlot = owner?.slot ?? null;
  const panelRef = React.useRef<HTMLDivElement>(null);
  // The panel as the owner of its nested popups (hostNestedPopups): known once the panel element exists.
  const [panelNode, setPanelNode] = React.useState<HTMLDivElement | null>(null);
  const nestedOwner = React.useMemo<LayerOwner | null>(
    () => (hostNestedPopups && panelNode ? { kind: 'page', depth: 0, slot: panelNode } : null),
    [hostNestedPopups, panelNode],
  );
  React.useLayoutEffect(() => {
    if (panelRef.current && anchorRef.current) popupOwners.set(panelRef.current, anchorRef.current);
  });
  const [mounted, setMounted] = React.useState(false);
  const [placement, setPlacement] = React.useState<PopupPlacement | null>(null);
  React.useEffect(() => setMounted(true), []);

  const reposition = React.useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    // Measure the natural size: drop the cap, read, and let the state re-apply it.
    const previousMax = panel.style.maxHeight;
    const previousScrollTop = panel.scrollTop;
    panel.style.maxHeight = 'none';
    const natural = { width: panel.offsetWidth, height: panel.scrollHeight };
    panel.style.maxHeight = previousMax;
    // Removing the cap can clamp scrollTop to zero; keep the person's place after measurement.
    panel.scrollTop = previousScrollTop;
    const rect = anchor.getBoundingClientRect();
    if (matchAnchorWidth) panel.style.minWidth = `${Math.round(rect.width)}px`;
    const next = computePopupPlacement({
      anchor: rect,
      panel: natural,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      prefer,
      align,
      gap,
    });
    setPlacement((prev) =>
      prev && prev.top === next.top && prev.left === next.left && prev.maxHeight === next.maxHeight && prev.side === next.side
        ? prev
        : next,
    );
  }, [anchorRef, prefer, align, gap, matchAnchorWidth]);

  React.useLayoutEffect(() => {
    if (!open || !mounted) return;
    reposition();
    // Only an ancestor scroll moves the anchor. A scroll within this popup must keep its viewport in place.
    const onScroll = (event: Event) => {
      if (event.target instanceof Node && panelRef.current?.contains(event.target)) return;
      reposition();
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', onScroll, true);
    const stopObserving = observeResizeRaf([panelRef.current, anchorRef.current], reposition);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', onScroll, true);
      stopObserving();
    };
  }, [open, mounted, reposition, anchorRef]);

  // Focus entry, once per opening, after the panel has been measured (it is `visibility: hidden` until then, and a
  // hidden element cannot take focus).
  const measured = placement !== null;
  const focusedThisOpening = React.useRef(false);
  React.useEffect(() => {
    if (!open) {
      focusedThisOpening.current = false;
      return;
    }
    if (!initialFocus || !measured || focusedThisOpening.current) return;
    const panel = panelRef.current;
    if (!panel) return;
    focusedThisOpening.current = true;
    // The style change that un-hides the panel can still read as `visibility: hidden` here: under
    // prefers-reduced-motion globals.css gives every element `transition-duration: 0.01ms`, so the visibility change
    // is a (tiny) transition still at its start on this frame. Real Chrome showed it (screenshot run 2026-10-24):
    // nothing was tabbable yet and focus stayed on the summary. Retry on the next frames until focus is inside.
    let frame = 0;
    let handle = 0;
    const tryFocus = () => {
      if (!panel.isConnected || panel.hidden || panel.contains(document.activeElement)) return;
      getTabbableElements(panel)[0]?.focus();
      if (!panel.contains(document.activeElement) && frame++ < 30) handle = requestAnimationFrame(tryFocus);
    };
    tryFocus();
    return () => cancelAnimationFrame(handle);
  }, [open, measured, initialFocus]);

  // Dismissal (docs/THEME.md 2.10, the layerStack adapter): while open, a popup that takes Escape is a non-modal layer
  // of the ONE stack. Escape reaches only the top layer (window capture, before Radix's document handlers, so a popup
  // inside a dialog no longer closes the dialog with it, and nested popups close one at a time), and a modal that opens
  // closes it first (reason "explicit": onOutside when given, so focus is not pulled back to the trigger).
  // Pushed once the panel is mounted, so a popup opened from inside another one sits above it.
  const escapeRef = React.useRef(onEscape);
  escapeRef.current = onEscape;
  const outsideRef = React.useRef(onOutside);
  outsideRef.current = onOutside;
  const takesEscape = !!onEscape;
  const layerId = React.useId();
  React.useEffect(() => {
    if (!open || !mounted || !takesEscape) return;
    return pushLayer({
      id: layerId,
      modal: false,
      get trigger() {
        return anchorRef.current;
      },
      get content() {
        return panelRef.current;
      },
      // A popup this panel hosts (a Select list, a menu) closes itself on its own Escape first, not this panel (G18).
      yieldsEscape(event) {
        if (!hostNestedPopups || !(event.target instanceof Element)) return false;
        const nested = event.target.closest('[data-popup-owned]');
        return !!nested && nested !== panelRef.current && !!panelRef.current?.contains(nested);
      },
      close(reason) {
        if (reason === 'escape') escapeRef.current?.();
        else if (reason === 'explicit') (outsideRef.current ?? escapeRef.current)?.();
        // 'outside-pointer' stays with the listener below: any button, exactly as before.
      },
    });
  }, [open, mounted, takesEscape, layerId, anchorRef, hostNestedPopups]);

  // Outside press: unchanged (any button, the anchor and the panel excluded). The layer stack's own outside handling
  // only acts on a primary press of the top layer; this keeps every caller's existing behaviour.
  React.useEffect(() => {
    if (!open || !onOutside) return;
    const onPointerDown = (event: Event) => {
      if (!(event.target instanceof Node)) return;
      if (panelRef.current?.contains(event.target)) return;
      if (anchorRef.current?.contains(event.target)) return;
      onOutside();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, onOutside, anchorRef]);

  if (!mounted) return null;
  return createPortal(
    <div
      ref={(node) => {
        panelRef.current = node;
        if (externalPanelRef) externalPanelRef.current = node;
        if (hostNestedPopups && node) setPanelNode(node);
      }}
      role={role}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      hidden={!open}
      data-popup-portal=""
      data-testid={testId}
      data-popup-side={placement?.side}
      onKeyDown={(event) => {
        if (event.key !== 'Tab' || !onEscape) return;
        const items = getTabbableElements(event.currentTarget);
        if (items.length === 0) return;
        const active = document.activeElement;
        const leaving = event.shiftKey ? active === items[0] : active === items[items.length - 1];
        if (leaving) {
          event.preventDefault();
          onEscape();
        }
      }}
      data-popup-owned={ownerSlot ? '' : undefined}
      className={
        surface === 'standard' || ownerSlot
          ? cn('fixed overflow-y-auto overscroll-contain', surface === 'standard' && cn(overlay.popupSurface, overlay.popupMaxWidth), ownerSlot && 'z-page-layer', className)
          : `fixed overflow-y-auto overscroll-contain ${className ?? ''}`
      }
      style={{
        top: placement?.top ?? 0,
        left: placement?.left ?? 0,
        maxHeight: placement?.maxHeight,
        // Hidden until measured: no flash at 0,0.
        visibility: open && placement === null ? 'hidden' : undefined,
      }}
    >
      {/* Wrapped whenever the prop is set (never toggled), so the children are not remounted when the panel appears. */}
      {hostNestedPopups ? <LayerOwnerProvider value={nestedOwner ?? owner}>{children}</LayerOwnerProvider> : children}
    </div>,
    container ?? ownerSlot ?? document.body,
  );
}

/**
 * For a dropdown's own "click outside closes me" handler: a press inside a
 * PORTALLED popup is not inside the dropdown's DOM subtree any more, but it
 * still belongs to that dropdown. Without this the press closes the dropdown
 * (and unmounts the menu) before the click on the item can land.
 */
export function isInsideAnchoredPopup(anchor: Element | null, target: EventTarget | null): boolean {
  if (!anchor || !(target instanceof Element)) return false;
  const panel = target.closest('[data-popup-portal]');
  if (!panel) return false;
  const owner = popupOwners.get(panel);
  return owner !== undefined && (owner === anchor || anchor.contains(owner));
}

export interface PortalMenuProps {
  panelRef?: React.MutableRefObject<HTMLDivElement | null>;
  className?: string;
  children: React.ReactNode;
  testId?: string;
  role?: string;
  /** Default 'bottom': a dropdown opens below its trigger and flips if cramped. */
  prefer?: 'top' | 'bottom';
  align?: 'start' | 'end';
  gap?: number;
  onClose?: () => void;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  /** "standard": the shared popup surface (see PopupPortal). */
  surface?: 'none' | 'standard';
}

/**
 * Drop-in for "an absolutely positioned panel inside a `relative` wrapper":
 * render it where the old `<div className="absolute ...">` was. The wrapper
 * (its DOM parent) is the anchor. Mount it only while open.
 */
export function PortalMenu({
  panelRef,
  className,
  children,
  testId,
  role,
  prefer = 'bottom',
  align = 'start',
  gap = 4,
  onClose,
  onMouseEnter,
  onMouseLeave,
  surface,
}: PortalMenuProps) {
  const marker = React.useRef<HTMLSpanElement>(null);
  const anchorRef = React.useMemo<React.RefObject<HTMLElement | null>>(
    () => ({
      get current() {
        return marker.current?.parentElement ?? null;
      },
      set current(_value: HTMLElement | null) {},
    }),
    [],
  );
  return (
    <>
      <span ref={marker} hidden aria-hidden="true" />
      <PopupPortal
        anchorRef={anchorRef}
        panelRef={panelRef}
        open
        prefer={prefer}
        align={align}
        gap={gap}
        testId={testId}
        onEscape={onClose}
        className={className}
        role={role}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        surface={surface}
      >
        {children}
      </PopupPortal>
    </>
  );
}
